import React from 'react'
import { formatEur } from './networkUtils'

/**
 * Division KPI Bar — shows savings breakdown by division with clickable segments.
 * Updates live when preference changes; arrows show delta vs. baseline.
 */
export default function DivisionKPIBar({ opportunities, divisionPreference, onPreferenceChange, siteToDivision, baselineOpportunities, params }) {
  if (!siteToDivision || !Object.keys(siteToDivision).length) return null

  // Tier-aware capture rate
  const capRate = (tier) => {
    const captureA = params?.captureA ?? 1.0
    const captureB = params?.captureB ?? 0.5
    const captureC = params?.captureC ?? 0.2
    return tier === 'A' ? captureA : tier === 'C' ? captureC : captureB
  }

  // Aggregate savings by division from current opportunities (capture-rate adjusted)
  const divTotals = {}
  opportunities.forEach(opp => {
    if (opp.quarantined) return
    const rate = capRate(opp.evidenceTier)
    const ds = opp.divisionSavings || {}
    Object.entries(ds).forEach(([div, savings]) => {
      divTotals[div] = (divTotals[div] || 0) + savings * rate
    })
  })

  // Baseline totals (when no preference active) for showing deltas
  const baselineTotals = {}
  if (baselineOpportunities && divisionPreference) {
    baselineOpportunities.forEach(opp => {
      if (opp.quarantined) return
      const rate = capRate(opp.evidenceTier)
      const ds = opp.divisionSavings || {}
      Object.entries(ds).forEach(([div, savings]) => {
        baselineTotals[div] = (baselineTotals[div] || 0) + savings * rate
      })
    })
  }

  const divisions = Object.keys(divTotals).sort((a, b) => (divTotals[b] || 0) - (divTotals[a] || 0))
  const grandTotal = Object.values(divTotals).reduce((s, v) => s + v, 0)
  if (grandTotal <= 0 || divisions.length === 0) return null

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Savings by Division</span>
          {divisionPreference && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
              Optimizing for {divisionPreference}
            </span>
          )}
        </div>
        {divisionPreference && (
          <button
            onClick={() => onPreferenceChange(null)}
            className="text-[10px] text-gray-400 hover:text-gray-600 underline"
          >
            Reset to equal
          </button>
        )}
      </div>

      {/* Segmented bar */}
      <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 mb-3">
        {divisions.map(div => {
          const pct = (divTotals[div] / grandTotal) * 100
          const isPreferred = div === divisionPreference
          return (
            <div
              key={div}
              onClick={() => onPreferenceChange(div === divisionPreference ? null : div)}
              className={`cursor-pointer transition-all ${isPreferred ? 'ring-2 ring-emerald-400 ring-offset-1' : 'hover:opacity-80'}`}
              style={{
                width: `${Math.max(pct, 3)}%`,
                backgroundColor: getDivisionColor(div, isPreferred),
              }}
              title={`${div}: ${formatEur(divTotals[div])}`}
            />
          )
        })}
      </div>

      {/* Legend with numbers */}
      <div className="flex flex-wrap gap-4">
        {divisions.map(div => {
          const isPreferred = div === divisionPreference
          const delta = baselineTotals[div] != null ? divTotals[div] - baselineTotals[div] : null
          const deltaPct = baselineTotals[div] > 0 ? Math.round((delta / baselineTotals[div]) * 100) : null
          return (
            <button
              key={div}
              onClick={() => onPreferenceChange(div === divisionPreference ? null : div)}
              className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-all ${
                isPreferred
                  ? 'bg-emerald-50 border border-emerald-200'
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: getDivisionColor(div, isPreferred) }}
              />
              <span className={`text-xs font-semibold ${isPreferred ? 'text-emerald-700' : 'text-gray-700'}`}>
                {div}
              </span>
              <span className={`text-xs font-bold ${isPreferred ? 'text-emerald-600' : 'text-gray-600'}`}>
                {formatEur(divTotals[div])}
              </span>
              {delta != null && delta !== 0 && (
                <span className={`text-[10px] font-medium ${delta > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {delta > 0 ? '↑' : '↓'}{Math.abs(deltaPct)}%
                </span>
              )}
            </button>
          )
        })}
      </div>

      <p className="text-[10px] text-gray-400 mt-2">
        Click a division to optimize. Opportunities recalculate to maximize savings for that division.
      </p>
    </div>
  )
}

const DIVISION_COLORS = {
  CMD: '#0d9488',
  MSD: '#6366f1',
  CAD: '#f59e0b',
}

function getDivisionColor(div, isPreferred) {
  const base = DIVISION_COLORS[div] || '#64748b'
  return isPreferred ? base : base + 'cc' // slightly transparent when not preferred
}
